import _, { isEqual } from "lodash";
import * as React from "react";
import { Stack } from "@mui/material";
import { CONTAINERS_NAME } from "../../utils/constants/common/componentName";
import { TUseXElement, useXElementUpdater } from "../../hooks/useXElement";
import { useXCommonChildren } from "../../hooks/useXCommon";
import { XPageProps } from "../../utils/interfaces/core/XPage";

// IMPORTING STATIC COMPONENTS
import XStack from "../containers/XStack/prod";
import XGrid from "../containers/XGrid/prod";
import XCarousel from "../containers/XCarousel/prod";
import XFooter from "../common/XFooter/prod";
import XHeader from "../common/XHeader/prod";
import XCategoryFilter from "../containers/XCategoryFilter";
import XProductDetails from "../containers/XProductDetails";
import ActionsDrawer from "../core/drawers/ActionsDrawer";

const XPageProd: React.FC<XPageProps> = ({
  initPage = { "/": [] },
  path = "/",
  initFooter = {},
  initHeader = {},
  initCommon = [],
  children,
}) => {
  const data = initPage[path] ?? [];

  const { xChildren: xHeader, setXChildren: setXHeader } =
    useXCommonChildren("HEADER");

  const { xChildren: xFooter, setXChildren: setXFooter } =
    useXCommonChildren("FOOTER");

  React.useEffect(() => {
    // SET THE GENERAL HEADER DATA
    if (initCommon && (!xHeader || xHeader.length === 0)) {
      setXHeader(
        initCommon.filter((c) => {
          if (!c.config) return false;

          return c.config.index.startsWith("HEADER");
        })
      );
    }

    // SET THE GENERAL FOOTER DATA
    if (initCommon && (!xFooter || xFooter.length === 0)) {
      setXFooter(
        initCommon.filter((c) => {
          if (!c.config) return false;

          return c.config.index.startsWith("FOOTER");
        })
      );
    }
  }, [initCommon]);

  const content = (
    <React.Fragment>
      {data.map((child, i) => {
        return (
          <DynamicXElement
            key={i}
            child={child}
            index={child.config?.index ?? ""}
            position={i}
          />
        );
      })}
    </React.Fragment>
  );

  return (
    <Stack
      id="XPage"
      sx={{
        width: "100vw",
        minHeight: "100vh",
        display: "block",
      }}
    >
      <XHeader initData={initHeader} />
      {children ?? content}
      <XFooter initData={initFooter} />
      <ActionsDrawer />
    </Stack>
  );
};

export default XPageProd;

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const DynamicXElement: React.FC<{
  child: any;
  index: string;
  position: number;
  // elements: any[];
}> = React.memo(({ child, index, position }) => {
  const setXElement = useXElementUpdater<any>(index);

  // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
  const setParent = useXElementUpdater<TUseXElement<any>>(
    child.config.childOf ?? "NO_PARENT"
  );

  // const setFooter = useXFooterUpdater(index);

  React.useEffect(() => {
    setParent((old) => {
      const elementsIndexes = old.elementsIndexes
        ? [...old.elementsIndexes, index]
        : [index];

      return { ...old, elementsIndexes: _.uniq(elementsIndexes) };
    });

    setXElement({
      name: child.name,

      index: child.index ?? index,

      hierachy: child.hierachy ?? "XPage-" + child.name,

      value: child.value ?? child.props ?? { all: {} },

      elementsIndexes: child.elementsIndexes ?? [],

      hasChildren: child.hasChildren ?? CONTAINERS_NAME.includes(child.name),

      childOf: child.childOf ?? child.config.childOf ?? null,

      dataFetching: child.dataFetching,
    });
  }, [index, position]);

  if (child.config.childOf) return null;

  switch (child.name) {
    case "XStack":
      return <XStack {...child} />;

    case "XGrid":
      return <XGrid {...child} />;
    case "XCategoryFilter":
      return <XCategoryFilter {...child} />;

    case "XProductDetails":
      return <XProductDetails {...child} />;

    case "XCarousel":
      return <XCarousel {...child} />;

    default:
      return null;
  }
}, compareProps);
